import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft,
  Download,
  Upload,
  Send,
  CheckCircle2,
  Clock,
  FileText,
  Users,
  AlertTriangle,
  Loader2,
  FileEdit,
  Archive,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/app/components/ui/card';
import { Button } from '@/app/components/ui/button';
import { Badge } from '@/app/components/ui/badge';
import { Input } from '@/app/components/ui/input';
import { Label } from '@/app/components/ui/label';
import { Textarea } from '@/app/components/ui/textarea';
import {
  useControlledDocument,
  useSubmitForReview,
  useApproveDocument,
  useUploadRevision,
  useDocumentAcknowledgments,
  useDeleteControlledDocument,
} from '@/hooks/use-documents';
import { toast } from '@/app/components/ui/simple-toaster';
import { useOrgTimeZone } from '@/hooks/use-settings';
import { formatDateOnly, formatDateTimeInTimeZone } from '@/lib/date-utils';
import type { DocumentStatus, DocumentRevision } from '@/lib/types';
import { resolveMediaUrl } from '@/lib/api-client';
import { usePermissions } from '@/hooks/use-permissions';
import { useAuth } from '@/contexts/auth-context';

const STATUS_CONFIG: Record<DocumentStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: typeof CheckCircle2 }> = {
  draft: { label: 'Draft', variant: 'secondary', icon: FileEdit },
  under_review: { label: 'Awaiting approval', variant: 'outline', icon: Clock },
  approved: { label: 'Approved', variant: 'default', icon: CheckCircle2 },
  superseded: { label: 'Superseded', variant: 'secondary', icon: Archive },
  archived: { label: 'Archived', variant: 'secondary', icon: Archive },
};

function StatusBadge({ status }: { status: DocumentStatus }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <Badge variant={config.variant} className="flex items-center gap-1">
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
}

function RevisionRow({ revision }: { revision: DocumentRevision }) {
  const timeZone = useOrgTimeZone();
  return (
    <div className="flex items-start justify-between gap-3 p-3 border rounded-lg">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="text-xs">v{revision.version_number}</Badge>
          <span className="text-xs text-gray-500">
            {formatDateTimeInTimeZone(revision.created_at, timeZone, { dateStyle: 'medium' })}
          </span>
        </div>
        <p className="text-sm text-gray-700 mt-1">{revision.change_summary}</p>
        <p className="text-xs text-gray-500 mt-0.5">
          Uploaded by {revision.uploaded_by_username}
          {revision.approved_by_username && ` · Approved by ${revision.approved_by_username}`}
        </p>
      </div>
      <a href={resolveMediaUrl(revision.file)} target="_blank" rel="noopener noreferrer">
        <Button variant="ghost" size="icon" className="shrink-0">
          <Download className="h-4 w-4" />
        </Button>
      </a>
    </div>
  );
}

export function DocumentDetailPage() {
  const timeZone = useOrgTimeZone();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const docId = Number(id);

  const { data: doc, isLoading, error } = useControlledDocument(docId);
  const { data: ackData } = useDocumentAcknowledgments(docId);
  const submitForReview = useSubmitForReview();
  const approveDocument = useApproveDocument();
  const uploadRevision = useUploadRevision();
  const deleteDocument = useDeleteControlledDocument();
  const { isAdmin } = usePermissions();
  const { user } = useAuth();

  const [showUpload, setShowUpload] = useState(false);
  const [revisionFile, setRevisionFile] = useState<File | null>(null);
  const [changeSummary, setChangeSummary] = useState('');

  const handleSubmitForReview = async () => {
    try {
      const updatedDocument = await submitForReview.mutateAsync(docId);
      toast.success(updatedDocument.review_route.message);
    } catch {
      toast.error('Failed to submit for review.');
    }
  };

  const handleDelete = () => {
    if (!window.confirm('Delete this draft controlled document?')) return;
    deleteDocument.mutate(docId, {
      onSuccess: () => navigate('/compliance/documents'),
      onError: () => toast.error('Failed to delete document.'),
    });
  };

  const handleApprove = async () => {
    try {
      await approveDocument.mutateAsync(docId);
      toast.success('Document has been approved.');
    } catch {
      toast.error('Failed to approve document.');
    }
  };

  const handleUploadRevision = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!revisionFile) return;

    const formData = new FormData();
    formData.append('file', revisionFile);
    formData.append('change_summary', changeSummary);

    try {
      await uploadRevision.mutateAsync({ id: docId, formData });
      toast.success('New version has been uploaded.');
      setShowUpload(false);
      setRevisionFile(null);
      setChangeSummary('');
    } catch {
      toast.error('Failed to upload revision.');
    }
  };

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 lg:p-8 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error || !doc) {
    return (
      <div className="p-4 sm:p-6 lg:p-8">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="pt-6">
            <p className="text-red-600">Failed to load document.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/documents')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-bold text-gray-900">{doc.title}</h1>
            <StatusBadge status={doc.status} />
            {doc.self_approved && <Badge variant="outline">Self-approved</Badge>}
            {doc.is_overdue && (
              <Badge variant="destructive" className="flex items-center gap-1">
                <AlertTriangle className="h-3 w-3" />
                Review Overdue
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
            {doc.document_number && <span className="font-mono">{doc.document_number}</span>}
            <span>v{doc.current_version}</span>
            <span>Owner: {doc.owner_username}</span>
          </div>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Main Content */}
        <div className="lg:col-span-2 space-y-6">
          {/* Document Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Document Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <dt className="text-gray-500">Category</dt>
                  <dd className="font-medium capitalize">{doc.category}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Current Version</dt>
                  <dd className="font-medium">v{doc.current_version}</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Review Interval</dt>
                  <dd className="font-medium">{doc.review_interval_days} days</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Next Review Due</dt>
                  <dd className="font-medium">
                    {doc.next_review_due
                      ? formatDateOnly(doc.next_review_due)
                      : 'Not set'}
                  </dd>
                </div>
                <div>
                  <dt className="text-gray-500">Retention Period</dt>
                  <dd className="font-medium">{doc.retention_years} years</dd>
                </div>
                <div>
                  <dt className="text-gray-500">Requires Acknowledgment</dt>
                  <dd className="font-medium">{doc.requires_acknowledgment ? 'Yes' : 'No'}</dd>
                </div>
                {doc.reviewer_username && (
                  <div>
                    <dt className="text-gray-500">Reviewer</dt>
                    <dd className="font-medium">{doc.reviewer_username}</dd>
                  </div>
                )}
                {doc.approved_by_username && (
                  <div>
                    <dt className="text-gray-500">Approved By</dt>
                    <dd className="font-medium">
                      {doc.approved_by_username}
                      {doc.approved_at && (
                        <span className="text-gray-500 font-normal">
                          {' '}on {formatDateTimeInTimeZone(doc.approved_at, timeZone, { dateStyle: 'medium' })}
                        </span>
                      )}
                    </dd>
                  </div>
                )}
              </dl>
              {doc.description && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-sm text-gray-600">{doc.description}</p>
                </div>
              )}
              {doc.related_clauses.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">Related QP-300 Clauses</h4>
                  <div className="flex flex-wrap gap-2">
                    {doc.related_clauses.map((clause) => (
                      <Badge key={clause} variant="outline">
                        Clause {clause}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Current File */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Current File</CardTitle>
                <a href={resolveMediaUrl(doc.file)} target="_blank" rel="noopener noreferrer">
                  <Button variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                </a>
              </div>
            </CardHeader>
          </Card>

          {/* Version History */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Version History</CardTitle>
                  <CardDescription>All revisions of this document</CardDescription>
                </div>
                <Button variant="outline" size="sm" onClick={() => setShowUpload(!showUpload)}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Revision
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {showUpload && (
                <form onSubmit={handleUploadRevision} className="mb-4 p-4 border rounded-lg bg-gray-50 space-y-3">
                  <div>
                    <Label htmlFor="revision_file">New Version File *</Label>
                    <Input
                      id="revision_file"
                      type="file"
                      onChange={(e) => setRevisionFile(e.target.files?.[0] || null)}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="change_summary">Change Summary *</Label>
                    <Textarea
                      id="change_summary"
                      value={changeSummary}
                      onChange={(e) => setChangeSummary(e.target.value)}
                      placeholder="What changed in this version?"
                      rows={2}
                      required
                    />
                  </div>
                  <div className="flex gap-2">
                    <Button type="submit" size="sm" disabled={uploadRevision.isPending}>
                      {uploadRevision.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                      Upload
                    </Button>
                    <Button type="button" variant="outline" size="sm" onClick={() => setShowUpload(false)}>
                      Cancel
                    </Button>
                  </div>
                </form>
              )}

              {doc.recent_revisions.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-6">No revisions yet</p>
              ) : (
                <div className="space-y-3">
                  {doc.recent_revisions.map((rev) => (
                    <RevisionRow key={rev.id} revision={rev} />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {doc.status === 'under_review' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Awaiting approval</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-gray-600">{doc.review_route.message}</p>
              </CardContent>
            </Card>
          )}
          {/* Actions */}
          <Card>
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {doc.status === 'draft' && (isAdmin || user?.id === doc.owner) && (
                <>
                  <Button variant="outline" className="w-full" asChild>
                    <Link to={`/compliance/documents/${doc.id}/edit`}>
                      <FileEdit className="h-4 w-4 mr-2" />
                      Edit Draft
                    </Link>
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleDelete}
                    disabled={deleteDocument.isPending}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {deleteDocument.isPending ? 'Deleting...' : 'Delete Draft'}
                  </Button>
                </>
              )}
              {doc.status === 'draft' && (
                <Button
                  className="w-full"
                  onClick={handleSubmitForReview}
                  disabled={submitForReview.isPending}
                >
                  {submitForReview.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Submit for Review
                </Button>
              )}
              {doc.status === 'under_review' && (
                <Button
                  className="w-full"
                  onClick={handleApprove}
                  disabled={approveDocument.isPending}
                >
                  {approveDocument.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                  )}
                  Approve Document
                </Button>
              )}
              {doc.requires_acknowledgment && doc.status === 'approved' && (
                <Link to={`/compliance/documents/${doc.id}/acknowledge`} className="block">
                  <Button variant="outline" className="w-full">
                    <FileText className="h-4 w-4 mr-2" />
                    Read & Acknowledge
                  </Button>
                </Link>
              )}
              <a href={resolveMediaUrl(doc.file)} target="_blank" rel="noopener noreferrer" className="block">
                <Button variant="outline" className="w-full">
                  <Download className="h-4 w-4 mr-2" />
                  Download Current
                </Button>
              </a>
            </CardContent>
          </Card>

          {/* Acknowledgment Tracker */}
          {doc.requires_acknowledgment && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Acknowledgments
                </CardTitle>
                <CardDescription>
                  v{doc.current_version} — {ackData?.acknowledged_count || 0} of{' '}
                  {(ackData?.acknowledged_count || 0) + (ackData?.not_acknowledged_count || 0)} users
                </CardDescription>
              </CardHeader>
              <CardContent>
                {ackData ? (
                  <div className="space-y-3">
                    {ackData.acknowledged.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Acknowledged</h4>
                        <div className="space-y-1">
                          {ackData.acknowledged.map((ack) => (
                            <div key={ack.id} className="flex items-center gap-2 text-sm">
                              <CheckCircle2 className="h-3.5 w-3.5 text-green-500" />
                              <span>{ack.username}</span>
                              <span className="text-xs text-gray-400">
                                {formatDateTimeInTimeZone(ack.acknowledged_at, timeZone, { dateStyle: 'medium' })}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {ackData.not_acknowledged.length > 0 && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-500 uppercase mb-2">Pending</h4>
                        <div className="space-y-1">
                          {ackData.not_acknowledged.map((user) => (
                            <div key={user.id} className="flex items-center gap-2 text-sm">
                              <Clock className="h-3.5 w-3.5 text-gray-400" />
                              <span className="text-gray-600">{user.username}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Loading...</p>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
